import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FutureBoardProgress from "./FutureBoardProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  FEELINGS, MEMO_MAX, buildCheckinInput, deleteFutureCheckin, formatLocalDate, isMemoTooLong, listFutureCheckins,
  parseFutureBoardError, recordFutureCheckin, supportsFor, timezoneNotice,
  type FeelingSupport, type FutureBoardFailure, type FutureCheckin, type FutureFeeling,
} from "@chronospark/shared";
import FailureAlert from "./FailureAlert";

type Translate = (key: string, options?: object) => string;

interface Props {
  open: boolean;
  cardId: number;
  accessToken?: string;
  language: string;
  t: Translate;
  onClose: () => void;
  /** 提案（押したときだけ呼ばれる）。編集や AI につなぐ */
  onSupport?: (support: FeelingSupport) => void;
  /** いま使えない提案（例：AI が無効のときの talk_ai）。ボタンごと出さない */
  hiddenSupports?: ReadonlySet<FeelingSupport>;
}

// 画面のない状態では、東京時間とする（サーバーが返す値が分かれば、それを表示する）。
const FALLBACK_TIMEZONE = "Asia/Tokyo";

const NONE: ReadonlySet<FeelingSupport> = new Set();

function FeelingDialog({ open, cardId, accessToken, language, t, onClose, onSupport, hiddenSupports = NONE }: Props) {
  const [feeling, setFeeling] = useState<FutureFeeling | null>(null);
  const [memo, setMemo] = useState("");
  const [saved, setSaved] = useState<FutureCheckin | null>(null);
  const [records, setRecords] = useState<FutureCheckin[]>([]);
  const [timezone, setTimezone] = useState(FALLBACK_TIMEZONE);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FutureBoardFailure | null>(null);

  const loadRecords = useCallback(async () => {
    if (!accessToken) return;
    try {
      const result = await listFutureCheckins(accessToken, cardId);
      setRecords(result.checkins);
      setTimezone(result.timezone || FALLBACK_TIMEZONE);
    } catch (error) {
      setFailure(parseFutureBoardError(error));
    }
  }, [accessToken, cardId]);

  // 開くたびに、前回の選択やメモを引き継がず、白紙から始める。
  useEffect(() => {
    if (!open) return;
    setFeeling(null);
    setMemo("");
    setSaved(null);
    setFailure(null);
    void loadRecords();
  }, [open, loadRecords]);

  const memoTooLong = isMemoTooLong(memo);

  const save = async () => {
    const input = buildCheckinInput(feeling, memo);
    if (!input || !accessToken || memoTooLong) return;
    setBusy(true);
    setFailure(null);
    try {
      const result = await recordFutureCheckin(accessToken, cardId, input);
      setSaved(result.checkin);
      setTimezone(result.checkin.timezone);
      await loadRecords();
    } catch (error) {
      setFailure(parseFutureBoardError(error));
    } finally {
      setBusy(false);
    }
  };

  const removeRecord = async (record: FutureCheckin) => {
    if (!accessToken) return;
    try {
      await deleteFutureCheckin(accessToken, cardId, record.id);
      setRecords(current => current.filter(item => item.id !== record.id));
    } catch (error) {
      setFailure(parseFutureBoardError(error));
    }
  };

  const supports = (feeling: FutureFeeling) => supportsFor(feeling).filter(support => !hiddenSupports.has(support));

  const chooseSupport = (support: FeelingSupport) => {
    onSupport?.(support);
    onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} aria-labelledby="future-feeling-title" maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle id="future-feeling-title">{t("futureBoard.feeling.title")}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {timezoneNotice(timezone, t)}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("futureBoard.feeling.optional")}
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          orientation="vertical"
          value={feeling}
          onChange={(_, value: FutureFeeling | null) => setFeeling(value)}
          aria-label={t("futureBoard.feeling.title")}
          disabled={busy || Boolean(saved)}
          sx={{ mb: 2 }}
        >
          {FEELINGS.map(option => (
            <ToggleButton key={option.key} value={option.key} sx={{ justifyContent: "flex-start", textTransform: "none" }}>
              {t(`futureBoard.feeling.${option.key}`)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={t("futureBoard.feeling.memoLabel")}
          value={memo}
          onChange={event => setMemo(event.target.value)}
          error={memoTooLong}
          helperText={memoTooLong ? t("futureBoard.validation.too_long", { max: MEMO_MAX }) : t("futureBoard.feeling.memoCount", { count: [...memo.trim()].length, max: MEMO_MAX })}
          disabled={busy || Boolean(saved)}
        />

        {failure && <Box sx={{ mt: 2 }}><FailureAlert failure={failure} t={t} onClose={() => setFailure(null)} /></Box>}

        {saved && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {t("futureBoard.feeling.saved")}
            </Alert>
            {supports(saved.feeling).length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("futureBoard.support.heading")}</Typography>
                <Stack spacing={1}>
                  {supports(saved.feeling).map(support => (
                    <Button key={support} variant="outlined" onClick={() => chooseSupport(support)} sx={{ justifyContent: "flex-start", textTransform: "none", textAlign: "left" }}>
                      {t(`futureBoard.support.${support}`)}
                    </Button>
                  ))}
                </Stack>
              </>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("futureBoard.feeling.history")}</Typography>
        {records.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t("futureBoard.feeling.historyEmpty")}</Typography>
        ) : (
          <Stack spacing={1}>
            {records.map(record => (
              <Stack key={record.id} direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">
                    {formatLocalDate(record.local_date, language)}　{t(`futureBoard.feeling.${record.feeling}`)}
                  </Typography>
                  {record.memo && (
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                      {record.memo}
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={() => removeRecord(record)} aria-label={t("futureBoard.feeling.deleteRecord")}>
                  <Icon fontSize="small">delete_outline</Icon>
                </IconButton>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {saved ? (
          <Button onClick={onClose}>{t("futureBoard.actions.close")}</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>{t("futureBoard.feeling.skip")}</Button>
            <Button onClick={save} disabled={busy || !feeling || memoTooLong} variant="contained">
              {busy && <FutureBoardProgress size={18} color="inherit" />}{t("futureBoard.feeling.save")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default FeelingDialog;
